import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthService } from '../services/authService';
import { Logo } from '../components/icons/Logo';
import toast from 'react-hot-toast';
import { Button } from '../components/ui';
import { motion, useReducedMotion, type Variants } from 'motion/react';
import { resolvePostAuthPath } from '../utils/postAuthRedirect';
import { useAuthStore } from '../stores/useAuthStore';

const RESEND_COOLDOWN_SECONDS = 60;
const RESEND_MAX_ATTEMPTS = 3;
const RESEND_STORAGE_KEY = 'verify_email_resend_state_v1';

interface ResendState {
    attempts: number;
    cooldownUntil: number;
}

const loadResendState = (): ResendState => {
    try {
        const raw = localStorage.getItem(RESEND_STORAGE_KEY);
        if (!raw) return { attempts: 0, cooldownUntil: 0 };
        const parsed = JSON.parse(raw);
        return {
            attempts: Number(parsed.attempts) || 0,
            cooldownUntil: Number(parsed.cooldownUntil) || 0,
        };
    } catch {
        return { attempts: 0, cooldownUntil: 0 };
    }
};

const saveResendState = (state: ResendState): void => {
    try {
        localStorage.setItem(RESEND_STORAGE_KEY, JSON.stringify(state));
    } catch {
        // localStorage may be unavailable (private mode); fail silently
    }
};

const VerifyEmailPage: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [resendAttempts, setResendAttempts] = useState(0);
    const [cooldownSeconds, setCooldownSeconds] = useState(0);
    const [verificationStatus, setVerificationStatus] = useState<{ emailVerified: boolean; email: string } | null>(null);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const prefersReducedMotion = useReducedMotion();

    // Page-load entrance variants: base=300ms, slow=500ms — numeric equivalents of
    // --snapfy-duration-base and --snapfy-duration-slow; easing mirrors --snapfy-ease-standard.
    const headingVariants: Variants = {
        hidden: prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: -12 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const } },
    };
    const cardVariants: Variants = {
        hidden: prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const, delay: 0.15 } },
    };

    useEffect(() => {
        const id = searchParams.get('id');
        const hash = searchParams.get('hash');
        const expires = searchParams.get('expires');
        const signature = searchParams.get('signature');

        if (id && hash && expires && signature) {
            handleVerification({ id, hash, expires, signature });
        } else {
            checkStatus();
        }
    }, []);

    useEffect(() => {
        const state = loadResendState();
        setResendAttempts(state.attempts);
        const remainingMs = state.cooldownUntil - Date.now();
        if (remainingMs > 0) {
            setCooldownSeconds(Math.ceil(remainingMs / 1000));
        }
    }, []);

    useEffect(() => {
        if (cooldownSeconds <= 0) return;
        const id = window.setInterval(() => {
            setCooldownSeconds(prev => Math.max(0, prev - 1));
        }, 1000);
        return () => window.clearInterval(id);
    }, [cooldownSeconds]);

    const handleVerification = async (params: { id: string; hash: string; expires: string; signature: string }) => {
        setIsLoading(true);
        try {
            const result = await AuthService.verifyEmailWithToken(params);
            if (result.verified) {
                toast.success('Email verificado! Seus 10 créditos grátis foram ativados.');
                localStorage.removeItem(RESEND_STORAGE_KEY);
                navigate(resolvePostAuthPath(useAuthStore.getState().user));
            } else {
                toast.error(result.message || 'Falha na verificação');
                checkStatus();
            }
        } catch (error: any) {
            console.error(error);
            toast.error('Link de verificação inválido ou expirado.');
            checkStatus();
        } finally {
            setIsLoading(false);
        }
    };

    const checkStatus = async () => {
        setIsLoading(true);
        try {
            const status = await AuthService.checkEmailVerification();
            setVerificationStatus(status);
            if (status.emailVerified) {
                toast.success('Email verificado! Seus 10 créditos grátis foram ativados.');
                localStorage.removeItem(RESEND_STORAGE_KEY);
                navigate(resolvePostAuthPath(useAuthStore.getState().user));
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleManualCheck = async () => {
        if (isChecking) return;
        setIsChecking(true);
        try {
            const status = await AuthService.checkEmailVerification();
            setVerificationStatus(status);
            if (status.emailVerified) {
                toast.success('Email verificado! Seus 10 créditos grátis foram ativados.');
                localStorage.removeItem(RESEND_STORAGE_KEY);
                navigate(resolvePostAuthPath(useAuthStore.getState().user));
            } else {
                toast('Email ainda não verificado. Confira sua caixa de entrada ou pasta de spam.', { icon: 'ℹ️' });
            }
        } catch (error) {
            console.error(error);
            toast.error('Não foi possível checar o status. Tente novamente.');
        } finally {
            setIsChecking(false);
        }
    };

    const isResendBlocked = resendAttempts >= RESEND_MAX_ATTEMPTS;
    const isResendDisabled = isResending || cooldownSeconds > 0 || isResendBlocked;

    const handleResend = async () => {
        if (isResendDisabled) return;
        setIsResending(true);
        try {
            await AuthService.resendVerificationEmail();
            toast.success('Email de verificação reenviado!');
            const newAttempts = resendAttempts + 1;
            const cooldownUntil = Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
            setResendAttempts(newAttempts);
            setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
            saveResendState({ attempts: newAttempts, cooldownUntil });
        } catch (error: any) {
            console.error(error);
            toast.error('Falha ao reenviar email.');
        } finally {
            setIsResending(false);
        }
    };

    const resendLabel = isResending
        ? 'Enviando...'
        : isResendBlocked
            ? 'Limite de tentativas atingido'
            : cooldownSeconds > 0
                ? `Aguarde ${cooldownSeconds}s para reenviar`
                : 'Reenviar Email de Verificação';

    const attemptsLabel = !isResendBlocked && resendAttempts > 0 && cooldownSeconds === 0
        ? `${RESEND_MAX_ATTEMPTS - resendAttempts} tentativa${RESEND_MAX_ATTEMPTS - resendAttempts === 1 ? '' : 's'} restante${RESEND_MAX_ATTEMPTS - resendAttempts === 1 ? '' : 's'}`
        : null;

    return (
        <div className="min-h-screen bg-snapfy-bg flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <motion.div
                className="sm:mx-auto sm:w-full sm:max-w-md"
                variants={headingVariants}
                initial="hidden"
                animate="visible"
            >
                <div className="flex justify-center">
                    <Logo className="h-12 w-auto" />
                </div>
                <h2 className="mt-6 text-center text-3xl font-extrabold text-snapfy-text-primary">
                    Verifique seu email
                </h2>
                <p className="mt-2 text-center text-sm text-snapfy-text-secondary">
                    {verificationStatus?.email ? (
                        <>Enviamos um link de verificação para <strong>{verificationStatus.email}</strong></>
                    ) : (
                        'Verifique sua caixa de entrada'
                    )}
                </p>
            </motion.div>

            <motion.div
                className="mt-8 sm:mx-auto sm:w-full sm:max-w-md"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
            >
                <div className="card bg-snapfy-surface py-8 px-4 shadow-card rounded-card sm:px-10 border border-snapfy-border text-center">
                    <div className="space-y-6">
                        {/* Credits info panel — bg-snapfy-primary-10 = primary/10 tint token */}
                        <div className="bg-snapfy-primary-10 border border-snapfy-primary/30 rounded-lg p-4">
                            <p className="text-snapfy-accent-soft font-semibold text-lg">
                                10 créditos grátis
                            </p>
                            <p className="text-snapfy-accent-soft/70 text-sm mt-1">
                                Verifique seu email para ativar seus créditos e começar a criar.
                            </p>
                        </div>

                        <p className="text-snapfy-text-secondary">
                            Se você não recebeu o email, clique no botão abaixo para reenviar.
                        </p>

                        <Button
                            variant="solid"
                            className="w-full"
                            onClick={handleResend}
                            disabled={isResendDisabled}
                            isLoading={isResending}
                        >
                            {resendLabel}
                        </Button>

                        {isResendBlocked && (
                            <p className="text-xs text-snapfy-error">
                                Você atingiu o limite de {RESEND_MAX_ATTEMPTS} tentativas. Caso ainda não tenha recebido, entre em contato com o suporte.
                            </p>
                        )}

                        {attemptsLabel && (
                            <p className="text-xs text-snapfy-text-secondary -mt-2">
                                {attemptsLabel}
                            </p>
                        )}

                        <div className="text-sm">
                            <button
                                onClick={handleManualCheck}
                                disabled={isChecking}
                                className="font-medium text-snapfy-primary hover:text-snapfy-accent-soft disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                            >
                                {isChecking && (
                                    <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                    </svg>
                                )}
                                {isChecking ? 'Verificando...' : 'Já verifiquei o email'}
                            </button>
                        </div>

                        <div className="text-sm border-t border-snapfy-border pt-4">
                            <Link to="/login" className="font-medium text-snapfy-text-secondary hover:text-snapfy-text-primary">
                                Voltar para Login
                            </Link>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default VerifyEmailPage;
